import React from 'react';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { TEAM } from '../../lib/constants';

const Team = () => (
  <section id="team" className="relative py-16 sm:py-20" data-testid="team-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="max-w-2xl">
        <span className="font-display uppercase tracking-[0.22em] text-[11px] text-[color:var(--neon-red)]">
          The Team
        </span>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl text-white tracking-tight">
          Decorated. Patient. Family.
        </h2>
        <p className="mt-4 text-white/75 leading-relaxed">
          The names parents in our reviews write about over and over again —
          the instructors who turn first-timers into Black Belts.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {TEAM.map((person, i) => (
          <motion.div
            key={person.id}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.05 * i }}
            className="panel rounded-2xl overflow-hidden group hover:border-[color:var(--neon-red)]/45 transition-colors"
            data-testid={`team-card-${person.id}`}
          >
            {/* Photo */}
            <div className="relative aspect-[4/5] overflow-hidden bg-[#121722]">
              {person.photo ? (
                <img
                  src={person.photo}
                  alt={person.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  style={{ objectPosition: 'center 22%' }}
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center font-display text-4xl tracking-[0.14em] text-white/40">
                  {person.initials}
                </div>
              )}
              {/* Bottom fade for legibility */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(to top, rgba(7,8,10,0.85) 0%, rgba(7,8,10,0.35) 35%, transparent 60%)',
                }}
              />
              {/* Subtle red glow ring on hover */}
              <div
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ boxShadow: 'inset 0 0 0 1px rgba(255,42,42,0.55), inset 0 0 32px rgba(255,42,42,0.15)' }}
              />
              {/* Role chip */}
              <div className="absolute left-3 top-3">
                <Badge variant="outline" className="border-[color:var(--neon-red)]/55 bg-black/60 backdrop-blur-sm text-white/90 font-mono-ui text-[10px] uppercase tracking-[0.16em]">
                  {person.role}
                </Badge>
              </div>
              {/* Name on photo bottom */}
              <div className="absolute left-4 right-4 bottom-3">
                <h3 className="font-display text-white text-xl sm:text-2xl tracking-tight leading-tight">
                  {person.name}
                </h3>
              </div>
            </div>
            {/* Bio */}
            <div className="p-5">
              <p className="text-sm text-white/75 leading-relaxed">{person.bio}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default Team;
